import { cs } from '../../lib/currency';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { DashboardLayout } from '../../components/DashboardLayout';
import {
  User,
  Calendar,
  BookOpen,
  CheckCircle,
  XCircle,
  TrendingUp,
  Award,
  AlertCircle,
  Eye,
  Lock,
  Loader2,
} from 'lucide-react';

const PlayerDashboard = () => {
  const navigate = useNavigate();
  const { _user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/player/dashboard');
      setDashboardData(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      </DashboardLayout>
    );
  }

  if (!dashboardData) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-800">Unable to load dashboard</h2>
          <Button onClick={fetchDashboard} className="mt-4">Retry</Button>
        </div>
      </DashboardLayout>
    );
  }

  const { player, parent, enrollments, programs, upcoming_classes, attendance, permissions } = dashboardData;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="player-dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-slate-900">Welcome, {player?.name}!</h1>
            <p className="text-slate-600">
              {permissions?.view_only ? (
                <span className="flex items-center gap-1 text-amber-600">
                  <Eye className="h-4 w-4" />
                  View-only access (Under 18)
                </span>
              ) : (
                <span className="flex items-center gap-1 text-green-600">
                  <CheckCircle className="h-4 w-4" />
                  Full access
                </span>
              )}
            </p>
          </div>
          
          {permissions?.view_only && (
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="py-3 px-4">
                <div className="flex items-center gap-2 text-amber-700">
                  <Lock className="h-4 w-4" />
                  <span className="text-sm">
                    Enrollments and payments are managed by your parent/guardian
                  </span>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card data-testid="stat-enrollments">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <BookOpen className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{enrollments?.length || 0}</p>
                  <p className="text-sm text-slate-500">Active Enrollments</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-programs">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Award className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{programs?.length || 0}</p>
                  <p className="text-sm text-slate-500">Programs</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-classes">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Calendar className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{upcoming_classes?.length || 0}</p>
                  <p className="text-sm text-slate-500">Classes</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-attendance">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <TrendingUp className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {attendance?.length > 0 
                      ? `${Math.round((attendance.filter(a => a.status === 'present').length / attendance.length) * 100)}%`
                      : 'N/A'}
                  </p>
                  <p className="text-sm text-slate-500">Attendance Rate</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="enrollments" className="space-y-4">
          <TabsList>
            <TabsTrigger value="enrollments">My Enrollments</TabsTrigger>
            <TabsTrigger value="schedule">Schedule</TabsTrigger>
            <TabsTrigger value="attendance">Attendance</TabsTrigger>
            {permissions?.can_enroll && (
              <TabsTrigger value="programs">Browse Programs</TabsTrigger>
            )}
            <TabsTrigger value="profile">My Profile</TabsTrigger>
          </TabsList>

          {/* Enrollments Tab */}
          <TabsContent value="enrollments">
            <Card>
              <CardHeader>
                <CardTitle>My Enrollments</CardTitle>
                <CardDescription>Your current program enrollments</CardDescription>
              </CardHeader>
              <CardContent>
                {enrollments?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Program</TableHead>
                        <TableHead>Class</TableHead>
                        <TableHead>Day/Time</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Fee</TableHead>
                        <TableHead>Frequency</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {enrollments.map((enrollment) => (
                        <TableRow key={enrollment.id}>
                          <TableCell className="font-medium">{enrollment.program_name}</TableCell>
                          <TableCell>{enrollment.class_name}</TableCell>
                          <TableCell>
                            {enrollment.day && enrollment.time 
                              ? `${enrollment.day} @ ${enrollment.time}`
                              : '-'}
                          </TableCell>
                          <TableCell>
                            <Badge variant={
                              enrollment.status === 'current' ? 'default' :
                              enrollment.status === 'approved' ? 'secondary' :
                              enrollment.status === 'paused' ? 'warning' :
                              'outline'
                            }>
                              {enrollment.status}
                            </Badge>
                          </TableCell>
                          <TableCell>{cs()}{enrollment.fee?.toFixed(2) || 'N/A'}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">
                              {enrollment.frequency === 'biannual' ? 'Bi-Annual' : enrollment.frequency}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <BookOpen className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>No active enrollments</p>
                    {permissions?.can_enroll && (
                      <Button 
                        className="mt-4"
                        onClick={() => navigate('/player/programs')}
                      >
                        Browse Programs
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Schedule Tab */}
          <TabsContent value="schedule">
            <Card>
              <CardHeader>
                <CardTitle>My Schedule</CardTitle>
                <CardDescription>Your upcoming classes</CardDescription>
              </CardHeader>
              <CardContent>
                {upcoming_classes?.length > 0 ? (
                  <div className="space-y-3">
                    {upcoming_classes.map((cls, index) => (
                      <div 
                        key={index}
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <Calendar className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium">{cls.class_name}</p>
                            <p className="text-sm text-slate-500">{cls.program_name}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-medium capitalize">{cls.day}</p>
                          <p className="text-sm text-slate-500">{cls.time}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Calendar className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>No scheduled classes</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Attendance Tab */}
          <TabsContent value="attendance">
            <Card>
              <CardHeader>
                <CardTitle>Attendance History</CardTitle>
                <CardDescription>Your recent attendance records</CardDescription>
              </CardHeader>
              <CardContent>
                {attendance?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Class</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {attendance.slice(0, 15).map((record, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            {new Date(record.date).toLocaleDateString()}
                          </TableCell>
                          <TableCell>{record.class_name || record.class_id}</TableCell>
                          <TableCell>
                            {record.status === 'present' ? (
                              <Badge className="bg-green-100 text-green-700">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Present
                              </Badge>
                            ) : (
                              <Badge variant="destructive">
                                <XCircle className="h-3 w-3 mr-1" />
                                Absent
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <TrendingUp className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>No attendance records yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Programs Tab (Adult only) */}
          {permissions?.can_enroll && (
            <TabsContent value="programs">
              <Card>
                <CardHeader>
                  <CardTitle>Available Programs</CardTitle>
                  <CardDescription>Browse and enroll in programs</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button onClick={() => navigate('/player/programs')}>
                    View All Programs
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Profile Tab */}
          <TabsContent value="profile">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5" />
                    My Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Name</p>
                      <p className="font-medium">{player?.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Age</p>
                      <p className="font-medium">{player?.age || 'N/A'} years</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Email</p>
                      <p className="font-medium">{player?.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Phone</p>
                      <p className="font-medium">{player?.phone || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Skill Level</p>
                      <p className="font-medium capitalize">{player?.skill_level || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Account Type</p>
                      <Badge variant={player?.is_adult ? 'default' : 'secondary'}>
                        {player?.is_adult ? 'Adult Player' : 'Junior Player'}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {parent && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User className="h-5 w-5" />
                      Parent/Guardian
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-slate-500">Name</p>
                        <p className="font-medium">{parent.name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Email</p>
                        <p className="font-medium">{parent.email}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Phone</p>
                        <p className="font-medium">{parent.phone || 'N/A'}</p>
                      </div>
                    </div>
                    <p className="text-sm text-slate-500 mt-4">
                      Contact your parent for enrollment changes or payment inquiries.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default PlayerDashboard;
